/**
 * Pipeline service — thin wrapper around pipeline store with quota checks.
 * All operations are local (localStorage) for now.
 * This is the future integration point for API calls when auth is added.
 */

import { usePipelineStore } from '@/store/pipeline.store';
import { useDailyQuotaStore } from '@/store/daily-quota.store';
import { cvService } from './cv.service';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';

export interface AddJobResult {
  success: boolean;
  job?: PipelineJob;
  error?: string;
}

export interface GenerateCVResult {
  success: boolean;
  cvId?: string;
  error?: string;
}

export const pipelineService = {
  /**
   * Add a job to the pipeline after checking the daily vacancy save quota.
   */
  addJobToPipeline(
    vacancyData: Omit<PipelineJob, 'id' | 'addedAt' | 'status' | 'notes'>
  ): AddJobResult {
    const quotaStore = useDailyQuotaStore.getState();

    if (!quotaStore.canSaveVacancy()) {
      const remaining = quotaStore.getRemainingVacancies();
      return {
        success: false,
        error: `Daily vacancy save limit reached. Remaining: ${remaining}. Try again tomorrow.`,
      };
    }

    const consumed = quotaStore.consumeVacancy();
    if (!consumed) {
      return {
        success: false,
        error: 'Failed to consume vacancy quota.',
      };
    }

    const pipelineStore = usePipelineStore.getState();
    const job = pipelineStore.addJob(vacancyData);

    return { success: true, job };
  },

  /**
   * Move a job to a new pipeline status.
   * Delegates directly to the pipeline store.
   */
  moveJob(jobId: string, status: PipelineStatus): void {
    const pipelineStore = usePipelineStore.getState();
    pipelineStore.moveJob(jobId, status);
  },

  /**
   * Generate a CV for a specific pipeline job.
   * Checks the daily generation quota before proceeding.
   */
  async generateCVForJob(jobId: string): Promise<GenerateCVResult> {
    const quotaStore = useDailyQuotaStore.getState();

    if (!quotaStore.canGenerate()) {
      const remaining = quotaStore.getRemainingGenerations();
      return {
        success: false,
        error: `Daily CV generation limit reached. Remaining: ${remaining}. Try again tomorrow.`,
      };
    }

    const pipelineStore = usePipelineStore.getState();
    const job = pipelineStore.getJobById(jobId);

    if (!job) {
      return {
        success: false,
        error: 'Job not found in pipeline.',
      };
    }

    try {
      // Generate CV for the vacancy linked to this pipeline job
      const result = await cvService.generateCVForVacancy(job.vacancyId);

      // Consume quota after successful generation
      const consumed = quotaStore.consumeGeneration();
      if (!consumed) {
        return {
          success: false,
          error: 'Failed to consume generation quota.',
        };
      }

      // Attach the generated CV to the pipeline job
      pipelineStore.attachCV(jobId, result.cv.id);

      return { success: true, cvId: result.cv.id };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'CV generation failed.';
      return { success: false, error: message };
    }
  },
};
